import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Phone, Hash, MapPin } from 'lucide-react';
import Card from '../../components/ui/Card';
import Badge from '../../components/ui/Badge';
import Avatar from '../../components/ui/Avatar';
import Button from '../../components/ui/Button';
import Table from '../../components/ui/Table';
import api from '../../services/api';

export default function EmployeeCard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [stats, setStats] = useState({});
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const [empRes, actRes] = await Promise.allSettled([
          api.get(`/v1/employees/${id}`),
          api.get(`/v1/employees/${id}/actions?page=1&per_page=20`),
        ]);
        if (empRes.status === 'fulfilled' && empRes.value?.data) {
          setEmployee(empRes.value.data.employee || empRes.value.data);
          setStats(empRes.value.data.stats || {});
        }
        if (actRes.status === 'fulfilled' && actRes.value?.data) setActions(actRes.value.data);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    }
    load();
  }, [id]);

  if (loading) return (
    <div style={{ textAlign: 'center', padding: 80 }}>
      <div style={{ width: 32, height: 32, borderRadius: '50%', border: '3px solid rgba(0,206,238,0.15)', borderTopColor: '#00CEEE', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
      <span style={{ color: '#94A3B8', fontSize: 13 }}>Загрузка...</span>
    </div>
  );
  if (!employee) return <div style={{ textAlign: 'center', padding: 80, color: '#EF4444', fontWeight: 500 }}>Сотрудник не найден</div>;

  const actionColumns = [
    { key: 'created_at', label: 'Время', render: v => <span style={{ color: '#94A3B8', fontSize: 12 }}>{v ? new Date(v).toLocaleString('ru-RU') : '\u2014'}</span> },
    { key: 'type', label: 'Тип', render: v => <Badge variant="primary">{v || '\u2014'}</Badge> },
    { key: 'description', label: 'Описание', render: v => <span style={{ fontSize: 13 }}>{v || '\u2014'}</span> },
    { key: 'result', label: 'Результат', render: v => <Badge variant={v === 'success' ? 'success' : 'error'}>{v === 'success' ? 'OK' : v || '\u2014'}</Badge> },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <Button variant="ghost" icon={<ArrowLeft size={15} />} onClick={() => navigate('/employees')} size="sm">Назад</Button>
        <span style={{ color: '#CBD5E1' }}>/</span>
        <span style={{ fontSize: 13, color: '#64748B' }}>{employee.name}</span>
      </div>

      <Card>
        <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <Avatar name={employee.name} size={68} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: '#0F172A', letterSpacing: '-0.02em' }}>{employee.name}</h2>
            <div style={{ display: 'flex', gap: 16, marginTop: 8, flexWrap: 'wrap' }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: '#64748B' }}><Hash size={13} />{employee.source_code}</span>
              {employee.phone && <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: '#64748B' }}><Phone size={13} />{employee.phone}</span>}
              {(employee.store_name || (employee.store && employee.store.name)) && <span style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: '#64748B' }}><MapPin size={13} />{employee.store_name || employee.store.name}</span>}
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
              {(employee.position_name || (employee.position && employee.position.name)) && <Badge variant="primary">{employee.position_name || employee.position.name}</Badge>}
              <Badge variant={employee.status === 'online' ? 'success' : 'gray'} dot>{employee.status === 'online' ? 'Онлайн' : 'Офлайн'}</Badge>
              <Badge variant={employee.is_active ? 'success' : 'error'}>{employee.is_active ? 'Активен' : 'Неактивен'}</Badge>
            </div>
          </div>
        </div>

        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))',
          gap: 16, marginTop: 24, paddingTop: 20, borderTop: '1px solid #F1F5F9',
        }}>
          {[
            { label: 'Задач выполнено', value: stats.tasks_completed || employee.tasks_completed || 0, color: '#00CEEE' },
            { label: 'ОСГ проверок', value: stats.osg_checks || 0, color: '#EF4444' },
            { label: 'Выкладок', value: stats.display_reports || 0, color: '#F59E0B' },
            { label: 'Сканирований', value: stats.scans || 0, color: '#8B5CF6' },
          ].map(s => (
            <div key={s.label} style={{ textAlign: 'center', padding: '12px 8px', background: '#F8FAFC', borderRadius: 12 }}>
              <div style={{ fontSize: 28, fontWeight: 800, color: s.color, letterSpacing: '-0.02em' }}>{s.value}</div>
              <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 2, fontWeight: 500 }}>{s.label}</div>
            </div>
          ))}
        </div>
      </Card>

      <Card title={`Действия (${actions.length})`} padding="0">
        <Table columns={actionColumns} data={actions} emptyText="Нет действий" pageSize={10} />
      </Card>
    </div>
  );
}
